/* eslint-disable no-tabs */
/* eslint-disable @typescript-eslint/indent */
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, map, Observable, tap } from 'rxjs';
import {
	ChangeDaum,
	ItemValleLab,
	ValleLabApiResponse,
} from '../../management-educational-institutions-sites/management-educational-institutions-sites.type';
import {
	AdditionalFiles,
	AspectData,
	AspectsManagementKey,
	ComponentPEI,
	EducationalProject,
	PeiObservations,
	StatusPEI,
} from '../educational-project.type';

export interface principlesResponse {
	data?: any;
}

@Injectable({
	providedIn: 'root',
})
export class PeiService {
	private historiesSubject = new BehaviorSubject<ChangeDaum[]>([]);
	histories$ = this.historiesSubject.asObservable();
	constructor(private http: HttpClient) {}

	set histories(histories: ChangeDaum[]) {
		this.historiesSubject.next(histories);
	}
	get histories(): ChangeDaum[] {
		return this.historiesSubject.getValue();
	}

	private idSubject = new BehaviorSubject<string>('');
	id$ = this.idSubject.asObservable();

	setId(id: string) {
		this.idSubject.next(id);
	}

	getId(): string | null {
		return this.idSubject.getValue();
	}

	getDataPei(): Observable<ComponentPEI[]> {
		return this.http
			.get<ValleLabApiResponse<ComponentPEI[]>>(`@valleLabApi/pei/component`)
			.pipe(map((response) => response.data));
	}

	getDataPeiAspect<T>(id: string): Observable<T> {
		return this.http
			.get<ValleLabApiResponse<T>>(
				`@valleLabApi/pei/aspect-ind/${id}/related-data`,
			)
			.pipe(map((response) => response.data));
	}
	getDataAspectByPeiIdAndAspectId<T>(
		peiId: string,
		aspectId: string,
	): Observable<AspectData<T>> {
		return this.http.get<AspectData<T>>(
			`@valleLabApi/pei/${peiId}/aspect-ind/${aspectId}`,
		);
	}
	downloadPEI(peiId: string): void {
		this.http
			.post(
				`@valleLabApi/pei/download`,
				{
					peiId: peiId,
				},
				{ responseType: 'blob' as const },
			)
			.subscribe((blob: Blob) => {
				const url = globalThis.URL.createObjectURL(blob);
				const a = document.createElement('a');
				a.href = url;
				a.download = 'PEI.pdf';
				document.body.appendChild(a);
				a.click();
				document.body.removeChild(a);
				globalThis.URL.revokeObjectURL(url);
			});
	}
	sendRevision(): Observable<any> {
		return this.http.put<any>('@valleLabApi/pei/public', {});
	}
	sendApprove(id: string): Observable<any> {
		return this.http.put<any>(`@valleLabApi/pei/${id}/approve`, {});
	}
	getStatusPei(): Observable<StatusPEI> {
		return this.http.get<StatusPEI>('@valleLabApi/pei/status');
	}
	postDataPeiAspect<T>(
		id: string,
		data: T,
		aspectKey: AspectsManagementKey = 'administrative-management',
		additionalFiles?: AdditionalFiles[],
	): Observable<any> {
		const formData = new FormData();
		formData.append('data', JSON.stringify(data));
		if (additionalFiles) {
			additionalFiles.forEach((file) => {
				if (Array.isArray(file.file)) {
					file.file.forEach((f) => {
						formData.append(file.name, f);
					});
				} else {
					formData.append(file.name, file.file);
				}
			});
		}
		return this.http
			.post<ValleLabApiResponse<any>>(
				`@valleLabApi/pei/${aspectKey}/aspect-ind/${id}`,
				formData,
			)
			.pipe(map((response) => response.data));
	}

	putDataPeiAspect<T>(
		id: string,
		data: T,
		aspectKey: AspectsManagementKey = 'administrative-management',
		additionalFiles?: { name: string; file: File | File[] }[],
		comments?: T,
	): Observable<any> {
		const formData = new FormData();
		formData.append('data', JSON.stringify(data));
		if (additionalFiles) {
			additionalFiles.forEach((file) => {
				if (Array.isArray(file.file)) {
					file.file.forEach((f) => {
						formData.append(file.name, f);
					});
				} else {
					formData.append(file.name, file.file);
				}
			});
		}
		if (comments) {
			const commentsData = JSON.stringify(comments);
			formData.append('comments', commentsData);
		}
		return this.http
			.put<ValleLabApiResponse<any>>(
				`@valleLabApi/pei/${aspectKey}/aspect-ind/${id}`,
				formData,
			)
			.pipe(map((response) => response.data));
	}
	getHistory(id: string, peiId: string): Observable<ChangeDaum[]> {
		return this.http
			.post<ValleLabApiResponse<ChangeDaum[]>>(
				`@valleLabApi/pei/aspect-ind/${id}/history`,
				{ peiId },
			)
			.pipe(
				map((response) => response.data),
				tap((histories) => {
					this.histories = histories;
				}),
			);
	}
	getRoleByInstitution(): Observable<ItemValleLab[]> {
		return this.http
			.get<ValleLabApiResponse<ItemValleLab[]>>(`@valleLabApi/filters/role`)
			.pipe(map((response) => response.data));
	}

	createGovernmentOrgan<T>(
		data: T,
		additionalFiles: AdditionalFiles[],
	): Observable<any> {
		const formData = new FormData();
		formData.append('data', JSON.stringify(data));
		if (additionalFiles) {
			additionalFiles.forEach((file) => {
				if (Array.isArray(file.file)) {
					file.file.forEach((f) => {
						formData.append(file.name, f);
					});
				} else {
					formData.append(file.name, file.file);
				}
			});
		}
		return this.http
			.post<ValleLabApiResponse<any>>(
				`@valleLabApi/pei/school-governing`,
				formData,
			)
			.pipe(map((response) => response.data));
	}

	getGovernmentOrgan<T>(): Observable<T> {
		return this.http
			.get<ValleLabApiResponse<T>>(`@valleLabApi/pei/school-governing`)
			.pipe(map((response) => response.data));
	}

	deleteGovernmentOrgan<T>(id: string): Observable<T> {
		return this.http
			.delete<ValleLabApiResponse<T>>(`@valleLabApi/pei/school-governing/${id}`)
			.pipe(map((response) => response.data));
	}

	getAllPEI(): Observable<EducationalProject[]> {
		return this.http
			.get<ValleLabApiResponse<EducationalProject[]>>(`@valleLabApi/pei/all`)
			.pipe(map((res) => res.data));
	}

	sendReject<T>(id: string, data: T): Observable<PeiObservations[]> {
		return this.http
			.post<ValleLabApiResponse<PeiObservations[]>>(
				`@valleLabApi/pei/${id}/reject`,
				data,
			)
			.pipe(map((response) => response.data));
	}
}
